import {
  Box,
  Divider,
  Flex,
  Link,
  Skeleton,
  useMediaQuery,
} from '@chakra-ui/react';
import BEIcon from '@molecules/BEIcon';
import Text from '@molecules/Text';
import styled from '@emotion/styled';
import ShoppingItem from './ShoppingItem';
import Tooltip from '@molecules/BEOffScreen/Tooltip';
import TaxesAndFees from '@view/components/Tooltips/TaxesAndFees';
import { BELoadingButton } from '@molecules/Button/Button';
import ScreenSize from '../../../constants/screen-sizes';
import { Routes as AppRoutes } from '@view/routes';
import { useMemo } from 'react';
import { useStore } from '@data/other/withStore';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { ShoppingCartProps } from '@view/components/Cards/ShoppingCart';
import ModalPaymentSecurity from '@view/components/Modals/PaymentSecutity';
import { useModal } from '@molecules/BEOffScreen';
import useFormat from '@business/utils/hooks/useFormat';
import { ThemeColors } from '@theme/foundations/colors';
import SkeletonShoppingItem from './SkeletonShoppingItem';

const StyledShoppingCartCard = styled.div`
  position: relative;
  background-color: white;
  width: 340px;
  min-width: 340px;
  border-radius: 16px;
  display:flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  box-shadow: 0px 4px 64px rgba(119, 130, 149, 0.1);
    border: 1px solid ${ThemeColors.neutral[300]};

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    width: 100%;
    height: calc(100vh - 40px);
  }
`;

const StyledShoppingItemsList = styled.div`
  width: 100%;
  height: 418px;
  overflow: auto;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    flex: 1;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  padding: 16px;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    position: relative;
    padding-top: 32px;
    align-items: flex-start;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 16px;
  gap: 4px;
  width: 100%;
  background-color: white;
  box-shadow: 0 -2px 25px -20px rgb(0 0 0 / 20%), 0 -4px 10px -6px rgb(0 0 0 / 20%);
  margin-bottom: 8px;

  .security-container {
    padding: 8px 0 0 0;
    @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
      padding: 18px 0px;
    }
  }
`;

export const StyledCloseButton = styled.button`
  display: none;

  &::selection {
      border: none;
  }

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    position: absolute;
    width: 48px;
    height: 48px;
    background-color: #FFFFFF;
    top: 16px;
    right: 0px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    cursor: pointer;
    border: none;
  }
`;

const ShoppingCartCard = ({ path = '', onCancel, onOk }: ShoppingCartProps) => {
  const modalPaymentSecutity = useModal(ModalPaymentSecurity, {});
  const {
    property: { accommodations, cart },
  } = useStore();
  const { t } = useTranslation();
  const { currency, date: formatDate } = useFormat();

  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);

  const confirmButtonText = useMemo(() => {
    if (path === AppRoutes.Accommodations) {
      return t('book-now');
    } else if (path === AppRoutes.Payment) {
      return t('pay-confirm');
    } else {
      return t('continue');
    }
  }, [path, t]);

  return (
    <StyledShoppingCartCard>
      <StyledHeader>
        <Text type="h3" weight="500">
          {t('reservation-summary')}
        </Text>
        {isMobile && (
          <Text>
            {formatDate(cart.checkIn, 'D MMM')}
            &nbsp;-&nbsp;
            {formatDate(cart.checkOut, 'D MMM')}
          </Text>
        )}
        {!isMobile && (
          <Flex gap="14px">
            <Text>{formatDate(cart.checkIn, 'MMM D, YYYY')}</Text>
            <BEIcon name="arrow-right" />
            <Text>{formatDate(cart.checkOut, 'MMM D, YYYY')}</Text>
          </Flex>
        )}
        <StyledCloseButton onClick={onCancel}>
          <BEIcon size="lg" name="close" weight="solid" />
        </StyledCloseButton>
      </StyledHeader>
      <Divider />
      <StyledShoppingItemsList>
        {!accommodations.model &&
          cart.items.length > 0 &&
          [...Array(cart.items.length)].map((v, index) => (
            <Box key={index}>
              <SkeletonShoppingItem />
              <Divider />
            </Box>
          ))}
        {accommodations.model &&
          cart.items.map((item) => {
            return (
              <Box key={item.id}>
                <ShoppingItem item={item} />
                <Divider />
              </Box>
            );
          })}
      </StyledShoppingItemsList>
      <StyledFooter>
        <Skeleton isLoaded={!!cart.totals}>
          <Flex justifyContent="space-between">
            <Flex gap="4px">
              <Text type="body">{t('taxes-and-fees')}</Text>
              {cart.totals && cart.totals.taxesAndFees > 0 && (
                <Tooltip
                  id="tooltip"
                  Component={TaxesAndFees}
                  vat={5}
                  city_tax={5}
                >
                  <BEIcon name="info-circle" weight="regular" size="md" />
                </Tooltip>
              )}
            </Flex>
            <Text>{currency(cart.totals?.taxesAndFees)}</Text>
          </Flex>
        </Skeleton>
        <Skeleton isLoaded={!!cart.totals}>
          <Flex justifyContent="space-between">
            <Text type="h4">Total</Text>
            <Text type="h4">{currency(cart.totals?.grandTotal)}</Text>
          </Flex>
        </Skeleton>
        <Skeleton isLoaded={!!cart.totals}>
          <Flex justifyContent="space-between" mb="12px">
            <Text type="h4">{t('deposit')}</Text>
            <Text type="h4">{currency(cart.totals?.deposit)}</Text>
          </Flex>
        </Skeleton>
        <BELoadingButton
          disabled={!cart.totals}
          size="lg"
          color="primary"
          onClick={onOk}
        >
          {confirmButtonText}
        </BELoadingButton>
        <Flex
          alignItems="center"
          justifyContent="center"
          gap="4px"
          className="security-container"
        >
          <BEIcon name="shield-check" />
          <Text>Secure online payment.</Text>
          <Link onClick={() => modalPaymentSecutity.open()}>
            <Text weight="500" color="primary">
              Learn more
            </Text>
          </Link>
        </Flex>
      </StyledFooter>
    </StyledShoppingCartCard>
  );
};

export default observer(ShoppingCartCard);
